/**
 * Signing in or signing up: Google (the button, or One Tap's "Continue as
 * …"), Facebook when it is switched on, or email and password below them.
 *
 * Shown by Layout in place of whatever page was asked for while nobody is
 * signed in; at "/" the landing page (features/landing) comes first, and its
 * buttons open this page on signing up or on signing in (initialMode). Left
 * to itself it opens on signing up, "Start your garden", when the visitor
 * came to start one: a "Start a garden" link (share page, garden page, the
 * website: ?start=1, or a share's ?ref), or a founding link (arrivalSource).
 * A share kept from an earlier visit no longer decides it: whether that one
 * still counts is the server's, at sign-up (audit A73). From the landing page it shows a
 * Back link to it (onBack). Otherwise on signing in. Either is
 * one link away from the other, under the form.
 *
 * Google comes first, one tap (not inside Instagram's or Facebook's own
 * browser, where Google refuses its sign-in: email only there, inAppBrowser);
 * then "or with your email", with each box
 * labelled: "Your name" (signing up only), "Email", "Password". Signing up
 * from someone's share says whose garden brought them ("Started from Amy's
 * garden").
 *
 * The page is in the browser's language (a Spanish browser gets Spanish), and
 * its "Español" / "English" switch chooses by hand (startLanguage); sign-up
 * takes that language to the new account (CompleteSignup).
 *
 * Starting a sign-in is noted (homePage), so that finishing it on one of the
 * app's own pages lands on Today rather than the page the phone last had
 * open; a link to somewhere in particular is kept.
 *
 * Requests: Firebase's own (through AuthContext); "Forgot password?" asks
 * POST /users/password-reset (api/users), and falls back to Firebase's own
 * reset mail when the server says it could not send ours. Mounted by Layout
 * in place of the page while nobody is signed in.
 *
 * @module LoginPage
 */

import { heldReferral } from '@/shared/api/shares';
import { keepArrival, keptFoundingCode, referredThisVisit, startsGarden } from '@/shared/lib/arrivalSource';
import { useGrowSettings } from '@/shared/lib/growSettings';
import { useState, FormEvent, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '@/shared/hooks/useAuth';
import Spinner from '@/shared/components/ui/Spinner';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { requestPasswordReset } from '@/shared/api/users';
import { markSigningIn } from '@/shared/lib/homePage';
import { chooseStartLanguage } from '@/shared/lib/startLanguage';
import { debugLog } from '@/config/config';
import { PRIVACY_URL } from '@/config/tunables/shared';
import { GoogleOneTap } from './GoogleOneTap';
import { inAppBrowser } from '@/shared/lib/inAppBrowser';
import { FIREBASE_AUTH_HANDLER_PATH, STANDALONE_DISPLAY_MEDIA_QUERY } from '@/config/tunables/ui';

/** A link-like button in the form's text: the other mode, the language, a forgotten password. */
const TEXT_BUTTON = 'font-semibold text-primary underline-offset-4 hover:underline disabled:opacity-50';

/** Google's mark. */
const GoogleMark = (): JSX.Element => (
  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" />
  </svg>
);

/** Facebook's mark. */
const FacebookMark = (): JSX.Element => (
  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#1877F2" d="M24 12.07C24 5.41 18.63 0 12 0S0 5.41 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.04V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.23 2.68.23v2.97h-1.51c-1.49 0-1.95.93-1.95 1.88v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z" />
  </svg>
);

/**
 * Whether the page opens on signing up: the visitor came to start a garden
 * (a "Start a garden" link, or a share's ?ref) or by a founding link.
 * The address is read again first: a "Start a garden" followed inside the app
 * (a share or garden page's link to /?start=1 or /?ref=CODE) arrives here
 * without the page loading, so main's reading never saw it.
 */
function opensOnSignUp(): boolean {
  try {
    keepArrival(window.location.search);
    return startsGarden() || Boolean(keptFoundingCode());
  } catch {
    return false;
  }
}

/** What the sign-in page is handed. */
interface LoginPageProps {
  /** Which way to open, when the visitor already chose (the landing page's buttons); otherwise by how they arrived. */
  initialMode?: 'signIn' | 'signUp';
  /** Goes back to the landing page; given only when the visitor came from it, and shows a Back link at the top. */
  onBack?: () => void;
}

/**
 * The sign-in and sign-up page: Google first, then email and password.
 *
 * @param props.initialMode - Opens on signing in or signing up when given.
 * @param props.onBack - Shows a Back link to the landing page when given.
 */
export default function LoginPage({ initialMode, onBack }: LoginPageProps = {}): JSX.Element {
  const [isLogin, setIsLogin] = useState(() => (initialMode ? initialMode === 'signIn' : !opensOnSignUp()));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  // Which window is being waited on, for the words under the spinner.
  const [via, setVia] = useState<'google' | 'facebook'>('google');
  const [notice, setNotice] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  const { signInWithEmail, signUpWithEmail, signInWithGoogle, signInWithFacebook, redirectError, sendResetEmailDirect } = useAuth();
  const settings = useGrowSettings();

  const { reactTranslate: t, i18n: { language } } = useTranslation();
  /** Inside Instagram's or Facebook's own browser, where Google's sign-in is refused: email only (inAppBrowser). */
  const [embedded] = useState(() => inAppBrowser());
  // The page's language, held so the switch redraws it at once.
  const [, setShownLanguage] = useState(language);
  const routerLocation = useLocation();
  const [referral] = useState<{ name: string | null } | null>(() => {
    try {
      // Only when this visit came through the share's link (so it was opened just now):
      // Grow's own buttons say nothing of it. Whether it credits the sign-up is the server's (A73).
      keepArrival(window.location.search);
      if (!referredThisVisit()) return null;
      const held = heldReferral();
      return held ? { name: held.name } : null;
    } catch { return null; }
  });

  /*
   * Somebody who has just deleted their account arrives here on purpose.
   *
   * Saying so on the page they land on, rather than in a toast raised by the
   * screen they were leaving, which had no Toaster left to show it.
   */
  useEffect(() => {
    if ((routerLocation.state as { accountDeleted?: boolean } | null)?.accountDeleted) {
      setNotice(t('profile.account.deleteDialog.doneBody'));
    }
  }, [routerLocation.state, t]);

  // Back from Google or Facebook without a sign-in: why.
  useEffect(() => {
    if (redirectError) setError(redirectError);
  }, [redirectError]);

  // Returning from a redirect: the sign-in is being finished, so wait for it.
  useEffect(() => {
    // If the URL has Firebase auth params, we're in the middle of auth
    const isAuthCallback = window.location.href.includes(FIREBASE_AUTH_HANDLER_PATH);
    if (isAuthCallback) {
      debugLog('LoginPage', 'Auth callback detected in LoginPage, setting redirecting state');
      setIsRedirecting(true);
    }
    debugLog('LoginPage', 'LoginPage mounted, checking environment variables:', {
      VITE_USE_MOCK_AUTH: import.meta.env.VITE_USE_MOCK_AUTH,
      IS_DEV: import.meta.env.DEV
    });
  }, []);

  /** Switches the page between English and Spanish, kept on this device for sign-up. */
  const switchLanguage = (): void => {
    const next = language.startsWith('es') ? 'en' : 'es';
    chooseStartLanguage(next);
    setShownLanguage(next);
  };

  /** Signs in, or signs up, with the email and password typed. */
  const handleEmailAuth = async (e: FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      setError(t('auth.fillAllFields'));
      return;
    }

    setLoading(true);
    setError(null);
    setNotice(null);
    markSigningIn();

    try {
      if (isLogin) {
        await signInWithEmail(email, password);
      } else {
        if (!displayName.trim()) {
          setError(t('auth.enterDisplayName'));
          setLoading(false);
          return;
        }
        await signUpWithEmail(email, password, displayName.trim());
      }
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError(t('auth.authError'));
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Emails a reset link for whatever is in the email box.
   *
   * Uses that box rather than opening a second one: somebody who has just
   * failed to sign in has already typed their address, and asking again is
   * asking them to prove they can type it twice.
   *
   * The confirmation is vague on purpose, and matches what the server says.
   * Telling somebody "no account with that address" here would undo the
   * server's care, since the answer would be on screen either way.
   */
  const handlePasswordReset = async () => {
    if (!email) {
      setError(t('auth.reset.needEmail'));
      return;
    }

    setIsResetting(true);
    setError(null);
    setNotice(null);

    try {
      const { useFallback } = await requestPasswordReset(email);

      // The server minted no link, or minted one and could not post it. It
      // never says which address failed, so this is safe to act on.
      if (useFallback) await sendResetEmailDirect(email);

      setNotice(t('auth.reset.sent'));
    } catch {
      // Even a failure here says the same thing. The request is rate limited
      // server-side, and an error that distinguishes addresses would be the
      // leak the endpoint exists to avoid.
      setNotice(t('auth.reset.sent'));
    } finally {
      setIsResetting(false);
    }
  };

  /**
   * Signs in with Google or Facebook: a window in a browser tab, or away and
   * back in the installed app.
   *
   * @param provider - Which.
   */
  const handleProviderAuth = async (provider: 'google' | 'facebook') => {
    setLoading(true);
    setError(null);
    markSigningIn();
    setVia(provider);
    debugLog('LoginPage', `Starting ${provider} authentication`);

    try {
      setIsRedirecting(true);
      const result = provider === 'google' ? await signInWithGoogle() : await signInWithFacebook();
      // Backed out of the window: the form again, with nothing to say.
      if (!result && !window.matchMedia?.(STANDALONE_DISPLAY_MEDIA_QUERY).matches) {
        setIsRedirecting(false);
        setLoading(false);
      }
    } catch (error) {
      console.error(`Error during ${provider} authentication:`, error);
      setIsRedirecting(false);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError(t('auth.googleAuthError'));
      }
      setLoading(false);
    }
  };

  // Show a loading spinner if we're waiting for a redirect to complete
  if (isRedirecting) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-foreground">
        <div className="flex flex-col items-center text-center" role="status">
          <Spinner size={48} />
          <p className="mt-4 text-muted-foreground">{t(via === 'facebook' ? 'auth.authenticatingFacebook' : 'auth.authenticating')}</p>
        </div>
      </div>
    );
  }

  /** Switches between signing in and signing up. */
  const otherMode = (): void => {
    setIsLogin(!isLogin);
    setError(null);
  };

  return (
    <div className="flex min-h-screen items-start justify-center bg-background px-4 py-6 text-foreground sm:items-center sm:py-12">
      <div className="w-full max-w-md space-y-4 rounded-lg border bg-card p-5 shadow-sm sm:p-8">
        {/*
          * The top row: Back to the landing page on the left, the language on
          * the right; the share's credit gets a line of its own under it (it
          * shared the row with the language and ran into Back on a phone).
          */}
        <div className="flex min-h-9 items-center justify-between gap-3">
          {onBack ? (
            <button type="button" onClick={onBack} className="inline-flex min-h-9 items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              {t('auth.backToStart')}
            </button>
          ) : <span />}
          {/* In the other language: "Español" on the English page, "English" on the Spanish. */}
          <button type="button" onClick={switchLanguage} className={`${TEXT_BUTTON} shrink-0 text-sm`} lang={language.startsWith('es') ? 'en' : 'es'}>
            {t('auth.otherLanguage')}
          </button>
        </div>
        {/* "Continue as …", Google's own prompt over the page (welcome.GOOGLE_ONE_TAP); never inside Instagram's or Facebook's browser. */}
        {settings['welcome.GOOGLE_ONE_TAP'] && !embedded && (
          <GoogleOneTap
            onStart={() => { setVia('google'); setIsRedirecting(true); }}
            onError={(message) => { setIsRedirecting(false); setError(message); }}
          />
        )}
        {/* Arrived from someone's shared plant: say whose garden brought them. */}
        {referral && !isLogin && (
          <p className="inline-block max-w-full rounded-full bg-muted px-3 py-1 text-sm font-semibold text-primary">
            {referral.name ? t('auth.startedFrom', { name: referral.name }) : t('auth.startedFromShare')}
          </p>
        )}

        <h1 className="font-grow text-2xl font-bold leading-tight">
          {isLogin ? t('auth.signIn') : t('auth.startGarden')}
        </h1>

        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
            {error}
          </div>
        )}

        {notice && (
          <div className="rounded-md border border-primary/40 bg-muted px-3 py-2 text-sm" role="status">
            {notice}
          </div>
        )}

        {/* Google first: one tap. Not inside Instagram's or Facebook's browser, where Google refuses it: email only there. */}
        <div className="space-y-2">
          {!embedded && (
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="w-full gap-2 bg-card"
              onClick={() => void handleProviderAuth('google')}
              disabled={loading}
            >
              <GoogleMark />
              {t('auth.continueWithGoogle')}
            </Button>
          )}
          {/* Facebook, once its app is live (welcome.FACEBOOK_SIGNIN). */}
          {settings['welcome.FACEBOOK_SIGNIN'] && (
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="w-full gap-2 bg-card"
              onClick={() => void handleProviderAuth('facebook')}
              disabled={loading}
            >
              <FacebookMark />
              {t('auth.facebook')}
            </Button>
          )}
        </div>

        {/* "Or with your email" only when there is something above it to be the other way. */}
        {(!embedded || settings['welcome.FACEBOOK_SIGNIN']) && <p className="text-center text-sm text-muted-foreground">{t('auth.orWithEmail')}</p>}

        <form className="space-y-3" onSubmit={handleEmailAuth}>
          {!isLogin && (
            <div className="space-y-1">
              <label htmlFor="display-name" className="text-sm font-medium">{t('auth.displayName')}</label>
              <Input
                id="display-name"
                name="displayName"
                type="text"
                autoComplete="given-name"
                required
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="h-11 text-base"
                disabled={loading}
              />
            </div>
          )}
          <div className="space-y-1">
            <label htmlFor="email-address" className="text-sm font-medium">{t('auth.emailAddress')}</label>
            <Input
              id="email-address"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11 text-base"
              disabled={loading}
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="password" className="text-sm font-medium">{t('auth.password')}</label>
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete={isLogin ? 'current-password' : 'new-password'}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-11 text-base"
              disabled={loading}
            />
          </div>

          <Button type="submit" size="lg" className="w-full" disabled={loading}>
            {loading ? t('auth.processing') : isLogin ? t('auth.signInAction') : t('auth.createGarden')}
          </Button>

          {/* Only when signing in. Offering it on the sign-up form would be
              answering a question nobody has yet asked. */}
          {isLogin && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => void handlePasswordReset()}
                disabled={loading || isResetting}
                className={`${TEXT_BUTTON} text-sm`}
              >
                {isResetting ? t('auth.reset.sending') : t('auth.reset.forgot')}
              </button>
            </div>
          )}
        </form>

        {/* The other way in, one link away. */}
        <p className="text-sm text-muted-foreground">
          {isLogin ? t('auth.noAccount') : t('auth.haveGarden')}{' '}
          <button type="button" onClick={otherMode} className={TEXT_BUTTON}>
            {isLogin ? t('auth.signUp') : t('auth.signInAction')}
          </button>
        </p>

        {/* The policy lives on the website; it covers Google sign-in and Photos. */}
        <p className="text-center text-xs text-muted-foreground">
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
            {t('auth.privacy')}
          </a>
        </p>
      </div>
    </div>
  );
}
